import { SubjectModalProps } from '../types';
import DialogModal from '../../../components/DialogModal';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import subjectAndGroupingApis from '../../../apis/subjects';
import DropdownSelect from '../../../components/DropdownSelect';
import { StyledButton } from '../../Exams/styled';
import {
  reportGroupOptions,
  reportSubGroupOptions,
  subjectTypeOptions
} from '../config/constant';
import TopLabelTextField from '../../../components/TopLabelTextField/TopLabelTextField';

const EditSubjectModal: React.FC<SubjectModalProps> = ({
  fetchData,
  open,
  onClose,
  onApiSuccessAndFailure,
  subject
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, setName] = useState<string>(subject?.name || '');
  const [type, setType] = useState<string>(subject?.type || '');
  const [isElective, setIsElective] = useState<boolean>(
    subject?.isElective || false
  );
  const [reportCardLabel, setReportCardLabel] = useState<string>(
    subject?.reportCardLabel || ''
  );
  const [code, setCode] = useState<string>(subject?.code || '');
  const [orderSequence, setOrderSequence] = useState<number | undefined>(
    subject?.orderSequence
  );
  const [reportGroup, setReportGroup] = useState<string>(
    subject?.reportGroup || ''
  );
  const [reportSubGroup, setReportSubGroup] = useState<string>(
    subject?.reportSubGroup || ''
  );

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  useEffect(() => {
    setName(subject?.name || '');
    setType(subject?.type || '');
    setIsElective(subject?.isElective || false);
    setReportCardLabel(subject?.reportCardLabel || '');
    setCode(subject?.code || '');
    setOrderSequence(subject?.orderSequence);
    setReportGroup(subject?.reportGroup || '');
    setReportSubGroup(subject?.reportSubGroup || '');
  }, [subject]);

  const handleSubmit = async () => {
    if (isSubmitting || !subject?.id) return;
    setIsSubmitting(true);

    const payload = {
      name: name || undefined,
      type: type || undefined,
      isElective,
      reportCardLabel: reportCardLabel || undefined,
      code: code || undefined,
      orderSequence: orderSequence || undefined,
      reportGroup: reportGroup || undefined,
      reportSubGroup: reportSubGroup || undefined
    };

    try {
      const response = await subjectAndGroupingApis.updateSubject(
        loggedInUser.school,
        subject.id,
        payload
      );
      if (response) {
        fetchData();
        onApiSuccessAndFailure(true, 'Subject updated successfully');
      }
    } catch (error: any) {
      onApiSuccessAndFailure(
        false,
        error.errors[0].message || 'Error updating subject'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <DialogModal
        open={open}
        onClose={onClose}
        title='Edit Subject'
        actions={
          <>
            <Button onClick={onClose} color='primary' variant='outlined'>
              Cancel
            </Button>
            <StyledButton onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Save'
              )}
            </StyledButton>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TopLabelTextField
            fullWidth
            id='name'
            name='name'
            label='Subject Name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            hasTopLabel
            sx={{ mb: 1 }}
          />
          <DropdownSelect
            label='Subject Type'
            placeholder='Select Subject Type'
            name='type'
            value={type}
            options={subjectTypeOptions}
            onChange={(e) => setType(e.target.value as string)}
            required
            sx={{ mb: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={isElective}
                onChange={(e) => setIsElective(e.target.checked)}
                name='isElective'
                color='primary'
              />
            }
            label='Is Elective'
          />

          <Typography variant='subtitle2' color='textSecondary'>
            ** All fields below this section are used for report card. So
            specify only if applicable, otherwise report-card design can go
            wrong. These fields are not applicable if subject is to be created
            for DCU.
          </Typography>

          <TopLabelTextField
            fullWidth
            id='reportCardLabel'
            name='reportCardLabel'
            label='Report Card Label'
            placeholder='Enter Report Card Label'
            value={reportCardLabel}
            onChange={(e) => setReportCardLabel(e.target.value)}
            hasTopLabel
            sx={{ mb: 1 }}
          />
          <TopLabelTextField
            fullWidth
            id='code'
            name='code'
            label='Subject Code'
            placeholder='Enter Subject Code'
            value={code}
            onChange={(e) => setCode(e.target.value)}
            hasTopLabel
            sx={{ mb: 1 }}
          />
          <TopLabelTextField
            fullWidth
            id='orderSequence'
            name='orderSequence'
            label='Order Sequence'
            placeholder='Enter Order Sequence'
            type='number'
            value={orderSequence === undefined ? '' : orderSequence}
            hasTopLabel
            onChange={(e) =>
              setOrderSequence(
                e.target.value === '' ? undefined : Number(e.target.value)
              )
            }
            slotProps={{
              htmlInput: {
                min: 0
              }
            }}
            sx={{ mb: 1 }}
          />
          <DropdownSelect
            label='Report Group'
            placeholder='Select Report Group'
            name='reportGroup'
            value={reportGroup}
            options={reportGroupOptions}
            onChange={(e) => setReportGroup(e.target.value as string)}
            sx={{ mb: 1 }}
          />
          <DropdownSelect
            label='Report Sub-Group'
            placeholder='Select Report Sub-Group'
            name='reportSubGroup'
            value={reportSubGroup}
            options={reportSubGroupOptions}
            onChange={(e) => setReportSubGroup(e.target.value as string)}
            sx={{ mb: 1 }}
          />
        </Box>
      </DialogModal>
    </>
  );
};

export default EditSubjectModal;
